import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { user } from 'src/StateObserver';
import PopupMatch2Base, {
  PopupMatch2BaseInitOptions,
} from '../components/PopupMatch2Base';
import { Match2Timer } from '../components/Match2Timer';
import Scroll from 'src/game/components/shared/Scroll';
import {
  Match2TournamentEntry,
  Match2TournamentEntryParams,
} from './Match2TournamentEntry';
import {
  Match2ModelTournamentData,
  Match2ModelTournamentPlayer,
} from 'src/replicant/match2/tournament/match2-tournament.actions';
import * as match2TournamentGetters from 'src/replicant/match2/tournament/match2-tournament.getters';
import uiConfig from 'src/lib/ui/config';
import { createUiTournamentHeader } from './match2-tournament.utils';
import sounds from 'src/lib/sounds';
import { match2TournamentRuleset } from 'src/replicant/match2/tournament/match2-tournament.ruleset';
import { AssetGroup } from '@play-co/timestep-core';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';
import { downscale } from 'src/conf/scale';

interface PopupMatch2TournamentInitOptions extends PopupMatch2BaseInitOptions {
  tournamentData: Match2ModelTournamentData;
}

export default class PopupMatch2Tournament extends PopupMatch2Base {
  private readonly scroll: Scroll<
    Match2TournamentEntryParams,
    Match2TournamentEntry
  >;
  private readonly timer: Match2Timer;
  private readonly scrollArea: ImageScaleView;
  private tournamentData?: Match2ModelTournamentData;
  private shouldClaimRewards = false;

  constructor(opts: { superview: View; close: (success: boolean) => void }) {
    const boxWidth = 640;
    const boxHeight = 960;
    const locale = StateObserver.getLocale() === 'en' ? 'en' : 'ja';

    super({
      superview: opts.superview,
      panelImageOpts: {
        ...uiConfig.popups.buruburu,
        width: boxWidth,
        height: boxHeight,
      },
      offsetX: -20,
      offsetY: -175,
      close: () => opts.close(false),
      title: {
        type: 'image',
        opts: {
          scaleMethod: 'contain',
          canHandleEvents: false,
          image: `assets/ui/match2/tournament/star_tournament_title_${locale}.png`,
          width: locale === 'en' ? downscale(583) : downscale(485),
          height: locale === 'en' ? downscale(199) : downscale(200),
          y: -10,
        },
      },
      cta: {
        localeText: () => i18n('match2.tournament.play').toLocaleUpperCase(),
        onClick: async () => {
          opts.close(true);
        },
        positioning: 'inside',
        offsetY: 25,
      },
      tracking: {
        feature: Feature.MATCH2.TOURNAMENT._,
        $subFeature: Feature.MATCH2.TOURNAMENT.POPUP,
      },
    });

    createUiTournamentHeader(this.panelView, 'star', boxWidth * 0.5, -80);

    this.scrollArea = new ImageScaleView({
      superview: this.panel,
      image: 'assets/ui/match2/common/list_scroll_area.png',
      width: boxWidth - 40,
      height: boxHeight - 240,
      x: 20,
      y: 120,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: {
          left: 22,
          right: 22,
        },
        vertical: {
          top: 22,
          bottom: 22,
        },
      },
    });

    this.timer = new Match2Timer({
      superview: this.panel,
      x: boxWidth * 0.5,
      y: 85,
      centerOnOrigin: true,
      endedText: i18n('match2.tournament.ended'),
    });
    this.timer.setRemainingTimeGetter(this.getRemainingTime.bind(this));
    this.timer.addListener('ViewRemoved', () => this.timer.stopAutoUpdate());

    this.scroll = new Scroll({
      superview: this.panel,
      createItem: this.createItem.bind(this),
      recycle: true,
      setData: this.setItemData.bind(this),
      rect: {
        x: this.scrollArea.style.x + 12,
        y: this.scrollArea.style.y + 5,
        width: this.scrollArea.style.width - 10,
        height: this.scrollArea.style.height - 10,
      },
    });
  }

  public init(args: PopupMatch2TournamentInitOptions) {
    super.init(args);
    this.tournamentData = args.tournamentData;
    this.scroll.setItems(this.getEntriesFromEvent(this.tournamentData));

    const sticky = new Match2TournamentEntry({
      width: this.scrollArea.style.width + 12,
      height: 90,
      x: this.scrollArea.style.x - 6,
      y: this.scrollArea.style.y + this.scrollArea.style.height - 90,
    });
    sticky.updateOpts({ height: sticky.style.height - 15, offsetY: -10 });
    sticky.setup({
      id: this.tournamentData.player.id,
      name: this.getPlayerName(this.tournamentData.player),
      avatar: this.getPlayerAvatar(this.tournamentData.player),
      frame: this.tournamentData.player.frame,
      score: this.tournamentData.player.score,
      scoreUpdatedAt: this.tournamentData.player.scoreUpdatedAt,
      position: this.tournamentData.currentRank,
      alt: true,
    });
    this.scroll.setStickyItem(sticky, this.tournamentData.currentRank - 1);

    this.updateState();
    this.timer.autoUpdate();
  }

  /**
   * Get the remaining time displayed by the tournament timer.
   *
   * @param currentNow Current timestamp supplied by the timer widget.
   * @returns Remaining tournament duration in milliseconds.
   */
  private getRemainingTime(currentNow: number) {
    // Use the scheduled tournament duration while leaderboard data is loading.
    if (!this.tournamentData) {
      return match2TournamentGetters.getRemainingTime(user(), currentNow);
    }

    // Completed tournaments should show the tournament-ended message.
    if (this.tournamentData.finalRank) {
      return 0;
    }

    // Active tournaments count down to the leaderboard event end date.
    return this.tournamentData.endDate - currentNow;
  }

  private getEntriesFromEvent(event: Match2ModelTournamentData) {
    const mainPlayerId = StateObserver.getState().user.id;
    const entries: Match2TournamentEntryParams[] = [];
    for (const player of event.leaderboard) {
      entries.push({
        id: player.id,
        name: this.getPlayerName(player),
        avatar: this.getPlayerAvatar(player),
        frame: player.frame,
        score: player.score,
        scoreUpdatedAt: player.scoreUpdatedAt,
        position: 0,
        me: player.id === mainPlayerId,
      });
    }

    return entries;
  }

  private getPlayerName(player: Match2ModelTournamentPlayer) {
    const user = StateObserver.getState().user;
    if (!player || !player.id || player.id === user.id) {
      // Get name from user profiles, otherwise use the generic fallback
      return (
        user.publicProfile?.name ||
        user.profile?.name ||
        i18n('match2.tournament.you')
      );
    }
    // Get opponent's name, otherwise use a generic name
    return player.name || i18n('match2.tournament.unknownPlayer');
  }

  private getPlayerAvatar(player: Match2ModelTournamentPlayer) {
    const user = StateObserver.getState().user;
    if (player.id === user.id) {
      return user.publicProfile?.photo ?? user.profile?.photo ?? GENERIC_AVATAR;
    }
    return player.avatar ?? GENERIC_AVATAR;
  }

  private createItem(
    superview: View,
    i: number,
    data: Match2TournamentEntryParams,
  ) {
    const entry = new Match2TournamentEntry({ superview });
    entry.setup({ ...data, position: i + 1 });
    return entry;
  }

  private setItemData(
    i: number,
    data: Match2TournamentEntryParams,
    view: Match2TournamentEntry,
  ) {
    view.setup({ ...data, position: i + 1 });
  }

  public updateState() {
    if (!this.tournamentData) {
      this.updateLoading();
    } else if (this.tournamentData.finalRank) {
      this.updateClaim();
    } else {
      this.updateOngoing();
    }
  }

  private updateLoading() {
    this.buttonCta!.localeText = () =>
      i18n('match2.tournament.play').toLocaleUpperCase();

    this.buttonCta!.setDisabled(true);
  }

  private updateOngoing() {
    this.buttonCta!.localeText = () =>
      i18n('match2.tournament.play').toLocaleUpperCase();
    this.buttonCta!.setDisabled(false);
  }

  private updateClaim() {
    if (this.shouldClaimRewards) return;
    this.shouldClaimRewards = true;
    this.buttonClose!.updateOpts({ visible: false });
    sounds.sfx.play('applause');
    // Prioritise current rank for testing purposes - in real scenarios, final rank should be equal to the current rank
    const rank =
      this.tournamentData!.currentRank ?? this.tournamentData!.finalRank ?? 0;
    const claimable = match2TournamentRuleset.getRewardsByRank(rank);
    this.buttonCta!.localeText = () =>
      i18n(claimable ? 'match2.tournament.claim' : 'basic.continue');
    this.buttonCta!.setDisabled(false);
  }

  protected getAssetsGroup() {
    // Tournament assets are not organised by type, so we load the whole folder.
    const urls = AssetGroup.constructURLs([`assets/ui/match2/tournament`]);
    return new AssetGroup(urls);
  }
}
